import type {
  ListTablePagination,
  ListTableProps,
  ListTableSort,
  OnListTableChange,
} from "./list-table.type";
import type { TableViewSort } from "@/entities/table-view";
import type { ListItem } from "@/modules/list";

import { Table } from "antd";
import classNames from "classnames";
import { useRef, useMemo } from "react";

import { useListContext } from "@/modules/list";
import { THEME_CLASSNAME_PREFIX } from "@/services/theme";

import ListTableActions from "./list-table-actions";
import ListTableBatchButtons, {
  isBatchModeActive,
} from "./list-table-batch-buttons";
import ListTableLazyHandler from "./list-table-lazy-handler";
import { convertTableParamsToListParams } from "./list-table.helper";
import {
  useListTableColumns,
  useListTableRowClassName,
  useListTableSelectedRows,
} from "./list-table.hook";

import "./list-table.style.less";

// ListTable should be used inside <ListProvider>
const ListTable = <Item extends ListItem>({
  columns = [],
  modelKey,
  onAddButton,
  addButtonText,
  extraActions,
  showExportButton,
  onBatchEditClick,
  onBatchDeleteClick,
  onBatchDissociateClick,
  onSelectedItemsChange,
  ...tableProps
}: ListTableProps<Item>) => {
  const tableRef = useRef<HTMLDivElement>(null);

  const {
    lazy,
    data,
    total,
    loading,
    sort,
    pagination: paginationParam,
    onParamsChange,
    defaultPageSize,
  } = useListContext<Item>();

  const { getRowClassName } = useListTableRowClassName(modelKey);

  const isBatchMode = isBatchModeActive<Item>({
    onBatchEditClick,
    onBatchDeleteClick,
    onBatchDissociateClick,
  });

  const { rowSelection, selectedItems } = useListTableSelectedRows<Item>({
    onSelectedItemsChange,
    isBatchMode,
  });

  const formattedColumns = useListTableColumns({ columns });

  const pagination = useMemo((): ListTablePagination | false => {
    if (lazy) return false;
    // TODO IMP-866 send back-end pagination data instead of list params pagination
    return {
      ...paginationParam,
      total,
      showSizeChanger: true,
      showTotal: (total, [from, to]) => `${from}-${to} / ${total}`,
    };
  }, [lazy, paginationParam, total]);

  const onListTableChange: OnListTableChange<Item> = (
    tablePagination,
    tableFilters,
    tableSort
  ) => {
    // Always fallback to empty array (and never defaultSort), to let the user:
    // - remove all sort params if needed
    // - have just 1 sort param at a time (useful when there is multiple items in defaultSort)
    const defaultSort: TableViewSort = [];

    const newParams = convertTableParamsToListParams({
      tableSort,
      tableFilters,
      tablePagination,
      currentListSort: sort,
      defaultPageSize,
      defaultSort,
    });

    onParamsChange(newParams);
  };

  return (
    <>
      <ListTableLazyHandler tableRef={tableRef} />
      <ListTableActions
        onAddButton={onAddButton}
        addButtonText={addButtonText}
        showExportButton={showExportButton}
        extraActions={extraActions}
        batchButtons={
          <ListTableBatchButtons
            divider={<div className="list-table-actions-divider" />}
            selectedItems={selectedItems}
            onBatchEditClick={onBatchEditClick}
            onBatchDeleteClick={onBatchDeleteClick}
            onBatchDissociateClick={onBatchDissociateClick}
          />
        }
      />
      <div
        ref={tableRef}
        className={classNames("list-table", {
          "list-table-lazy-loading": lazy ? true : false,
        })}
      >
        <Table
          rowKey="id"
          dataSource={data}
          loading={loading}
          onChange={(pagination, filters, sorter) => {
            const sort = sorter as ListTableSort<Item>;
            onListTableChange(pagination, filters, sort);
          }}
          rowClassName={getRowClassName}
          pagination={pagination}
          rowSelection={rowSelection}
          size="small"
          // Never use `scroll.y`, it breaks the table header and table body columns
          // alignment/overlap (because it actually create 2 separated tables, one
          // for thead and one for tbody...). For lazy loading we use `max-height`
          // on `.list-table--table-content instead.
          scroll={{ x: "max-content" }}
          {...tableProps}
          columns={formattedColumns}
          prefixCls={`${THEME_CLASSNAME_PREFIX}-table`} // Keep it, this is needed for the unit tests to work
          classNames={{
            body: {
              cell: "list-table--table-body-cell",
            },
            header: {
              wrapper: "list-table--table-header",
              cell: "list-table--table-header-cell",
            },
            content: "list-table--table-content",
          }}
        />
      </div>
    </>
  );
};

export default ListTable;
